/**
 * Session-import actions — resolution and commit for externally-run session
 * results. Every write goes through existing canonical stores/RPCs (App Layer
 * FR-5): items via the items store, the session via createSessionWithFeedback,
 * per-participant scores in the session's provenance, members matched by
 * exact email via the session-participants store, and the ranking + the
 * DRAFT → RUN_COMPLETE state change via the one governed run path,
 * `run_session` (publishing stays a separate human action).
 * Steering spec: PRD-session-results-import §5–§7.
 */

import {
  runSessionViaRpc,
  useSessionStore,
} from '@/stores/prioritization/sessionStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useSessionParticipantsStore } from '@/stores/prioritization/sessionParticipantsStore';
import { listWorkspaceMembers } from '@/lib/workspaceService';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { logChangeEvent, registerExternalId } from '@/lib/connectors';
import { CSV_CONNECTOR_TYPE } from '@/lib/dataCitizenship/csvMappingProfiles';
import { sweepItemsForClusters } from '@/lib/clusterRunner';
import type { TwinsItemInput } from '@/lib/llmTwinsService';
import { createSessionWithFeedback } from '@/lib/sessions/createSessionFlow';
import {
  matchParticipantsByEmail,
  participantItemScore,
  resolveItemMatches,
  type ImportedItem,
  type ItemResolution,
  type MemberEmail,
  type ParsedSessionImport,
  type ParticipantMatch,
} from './sessionImportParser';

export type { ItemResolution, MatchReason } from './sessionImportParser';

/** The imported artifact is a finished ranking; stack_ranking is the canonical
 *  ranking-family tool with no criteria/participant preconditions, so the
 *  imported session traverses the same gates a native one would. */
const IMPORT_TOOL_ID = 'stack-ranking';

/**
 * Deterministic in-wizard resolution (never AI), the shared matcher
 * (resolveItemMatches): id → external_id → case-insensitive exact title.
 *
 * Fetches the workspace pool first — on a cold visit to the app route the
 * items store is empty, and matching against an unloaded pool would silently
 * resolve everything to Create (re-importing the pool as duplicates).
 */
export async function resolveImportItems(items: ImportedItem[]): Promise<ItemResolution[]> {
  try {
    await useItemsStore.getState().fetchFromSupabase();
  } catch {
    // Matching degrades to create-only; the resolve step still shows every row.
  }
  const pool = useItemsStore
    .getState()
    .items.filter((i) => !i.exit_status || i.exit_status === 'active');
  return resolveItemMatches(items, pool);
}

/**
 * Which imported participants are workspace members — by exact email only
 * (owner ruling, roadmap S-12). Read failures leave everyone recorded by name.
 */
export async function matchImportParticipants(parsed: ParsedSessionImport): Promise<ParticipantMatch[]> {
  const workspaceId = getCurrentWorkspaceId();
  let members: MemberEmail[] = [];
  if (workspaceId && parsed.responses.some((r) => r.email)) {
    try {
      members = (await listWorkspaceMembers(workspaceId))
        .filter((m) => m.status === 'active')
        .map((m) => ({ userId: m.userId, email: m.user.email }));
    } catch {
      members = [];
    }
  }
  return matchParticipantsByEmail(parsed.responses, members);
}

export interface CommitSessionImportInput {
  sessionName: string;
  fileName: string;
  parsed: ParsedSessionImport;
  resolutions: ItemResolution[];
  /** From matchImportParticipants: who in the file is a workspace member. */
  participantMatches: ParticipantMatch[];
  userId: string;
  userName: string;
}

export interface CommitSessionImportResult {
  success: boolean;
  error?: string;
  sessionId?: string;
  createdItemIds: string[];
  linkedCount: number;
  /** Participants seated as session participants (matched members). */
  seatedCount?: number;
}

function newUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
}

export async function commitSessionImport(
  input: CommitSessionImportInput
): Promise<CommitSessionImportResult> {
  const { parsed, resolutions, userId, userName } = input;
  const workspaceId = getCurrentWorkspaceId();
  const failure = (error: string): CommitSessionImportResult => ({
    success: false,
    error,
    createdItemIds: [],
    linkedCount: 0,
  });
  if (!workspaceId) {
    return failure('No workspace found — open the app from inside a workspace.');
  }
  if (parsed.blockers.length > 0) {
    return failure('The file still has blocking problems — fix them before importing.');
  }

  const resolutionByKey = new Map(resolutions.map((r) => [r.key, r]));
  const itemsStore = useItemsStore.getState();

  // 1 · Items: link or create, in ranking order. Created items are tagged
  //     source 'session_import' so the sweep (and provenance) can scope to them.
  const itemIdByKey = new Map<string, string>();
  const createdItemIds: string[] = [];
  let linkedCount = 0;
  for (const item of parsed.items) {
    const resolution = resolutionByKey.get(item.key);
    if (resolution?.action === 'link' && resolution.matchItemId) {
      itemIdByKey.set(item.key, resolution.matchItemId);
      linkedCount += 1;
      continue;
    }
    const newId = newUuid();
    const ok = await itemsStore.createItem({
      id: newId,
      title: item.title,
      description: item.description ?? '',
      tags: [],
      source: 'session_import',
      ...(item.externalId ? { external_id: item.externalId } : {}),
    });
    if (!ok) {
      return failure(`Could not create the item "${item.title}" — nothing else was changed beyond the ${createdItemIds.length} item(s) already created.`);
    }
    itemIdByKey.set(item.key, newId);
    createdItemIds.push(newId);
    await logChangeEvent({
      workspaceId,
      entityType: 'item',
      entityId: newId,
      eventType: 'created',
      newValue: { from: 'session_import', externalId: item.externalId ?? null, title: item.title },
    });
    if (item.externalId) {
      await registerExternalId({
        workspaceId,
        entityType: 'item',
        internalId: newId,
        connectorType: CSV_CONNECTOR_TYPE,
        externalId: item.externalId,
      });
    }
  }

  const orderedItemIds = parsed.items.map((item) => itemIdByKey.get(item.key)!);

  // 2 · The session — canonical creator, honest provenance in tool_config.
  //     Per-participant scores (full-results shape) ride in the provenance
  //     payload: the canonical tool_session_responses table deliberately binds
  //     each response row to an authenticated principal (RLS:
  //     auth.uid() = participant_id, a uuid) — an accountability property we
  //     do not fake with synthetic ids. A participant whose email is a
  //     member's carries that member's user id, and is seated below.
  const userIdByParticipant = new Map(
    input.participantMatches.filter((m) => m.userId).map((m) => [m.participant, m.userId!])
  );
  const importedResponses =
    parsed.shape === 'full-results'
      ? parsed.responses.map((response) => ({
          participant: response.participant,
          email: response.email ?? null,
          userId: userIdByParticipant.get(response.participant) ?? null,
          itemScores: parsed.items.map((item) => ({
            itemId: itemIdByKey.get(item.key)!,
            title: item.title,
            weightedScore: participantItemScore(response, item.key, parsed.criteria),
            byCriterion: response.scores[item.key] ?? {},
          })),
        }))
      : [];

  const createResult = await createSessionWithFeedback({
    name: input.sessionName.trim(),
    facilitatorId: userId,
    createdBy: userId,
    intent: 'rank_priorities',
    sessionContextMode: 'app_linked',
    itemIds: orderedItemIds,
    selectedToolId: IMPORT_TOOL_ID,
    modeConfig: { timingMode: 'async' },
    toolConfig: {
      import: {
        source: 'file',
        fileName: input.fileName,
        shape: parsed.shape,
        rankingSource: parsed.rankingSource,
        importedAt: new Date().toISOString(),
        importedBy: userId,
        importedByName: userName,
        criteria: parsed.criteria,
        participants: parsed.participants,
        responses: importedResponses,
      },
    },
    workspaceId,
  });
  if (!createResult.ok) {
    return failure(createResult.error ?? 'The session could not be created.');
  }
  const session = createResult.session!;

  // Matched members take their seats before the run — the session's
  // participants are the people who scored it.
  let seatedCount = 0;
  for (const memberId of new Set(userIdByParticipant.values())) {
    const seat = await useSessionParticipantsStore.getState().addParticipant({
      sessionId: session.id,
      userId: memberId,
      role: 'contributor',
      addedBy: userId,
    });
    if (seat) seatedCount += 1;
  }

  // 3 · The ranking — durable results first, then the state change (§2.3a:
  //     the transition is aborted when persistence fails).
  const rankingOutput = {
    toolId: IMPORT_TOOL_ID,
    rankedItemIds: orderedItemIds,
    scores: Object.fromEntries(
      parsed.items
        .filter((item) => item.score !== null)
        .map((item) => [itemIdByKey.get(item.key)!, { total: item.score! }])
    ),
    generatedAt: new Date().toISOString(),
  };
  // One act, one write path (771): run_session writes ranking_output, the
  // RUN_COMPLETE state and the session_results rows in one server transaction,
  // so a refused import leaves no half-run session behind.
  const runResult = await runSessionViaRpc(session.id, rankingOutput);
  if (!runResult.success) {
    return failure(runResult.error ?? 'Could not save the imported results.');
  }
  await useSessionStore.getState().fetchFromSupabase();

  return { success: true, sessionId: session.id, createdItemIds, linkedCount, seatedCount };
}

// ── The reconciler: AI comes back around after commit ────────────────────────

export type SweepOutcome =
  | { ran: true; proposed: number }
  | { ran: false; reason: 'agent-off' | 'no-new-items' | 'nothing-to-compare' | 'error' };

/**
 * Post-commit Twins sweep, scoped to the import batch: every call pairs ALL
 * newly created items with a chunk of the existing pool, so a new item and
 * its likely twin always share a batch. Clusters that include at least one
 * new item are persisted through the existing Twins review flow — humans
 * merge or dismiss there; nothing merges automatically.
 */
export async function runImportSweep(createdItemIds: string[]): Promise<SweepOutcome> {
  if (createdItemIds.length === 0) return { ran: false, reason: 'no-new-items' };
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ran: false, reason: 'error' };

  const createdSet = new Set(createdItemIds);
  const allItems = useItemsStore
    .getState()
    .items.filter((i) => !i.exit_status || i.exit_status === 'active');
  const toInput = (i: (typeof allItems)[number]): TwinsItemInput => ({
    id: i.id,
    title: i.title,
    description: i.description ?? undefined,
  });
  const newItems = allItems.filter((i) => createdSet.has(i.id)).map(toInput);
  const existing = allItems.filter((i) => !createdSet.has(i.id)).map(toInput);
  if (newItems.length === 0) return { ran: false, reason: 'no-new-items' };

  // The Twins agent's sweep (clusterRunner): the new items with the most similar existing ones first.
  try {
    const result = await sweepItemsForClusters({
      workspaceId,
      targets: newItems,
      others: existing,
      rationaleSuffix: '(flagged during a session-results import)',
    });
    if (!result.ran) return { ran: false, reason: result.reason ?? 'error' };
    if (result.stats.proposed === 0 && result.stats.errors > 0) return { ran: false, reason: 'error' };
    return { ran: true, proposed: result.stats.proposed };
  } catch (err) {
    console.warn('[sessionImport] Twins sweep failed:', err);
    return { ran: false, reason: 'error' };
  }
}
